import { Link } from "react-router-dom";
import { ArrowUpRight, Radio } from "lucide-react";
import { motion } from "framer-motion";
import { SERVICES, COMPANY } from "@/lib/site-data";

// Add category/specs to each service for the futuristic catalog
const SERVICE_META = {
  "jarmuponyva-keszites": { cat: "GYÁRTÁS", spec: "600–900 g/m²" },
  "jarmu-ponyva-javitas": { cat: "SZERVIZ", spec: "Érkezési sorrendben" },
  reklamdekoracio: { cat: "DEKOR", spec: "Egyedi grafika" },
  "satrak-mobilraktarak": { cat: "GYÁRTÁS", spec: "ADR ponyva" },
  "eloteto-arnyekolo": { cat: "GYÁRTÁS", spec: "Fix / nyitható" },
  "medence-ponyvak": { cat: "GYÁRTÁS", spec: "Csomagajánlat" },
  "terasz-teliesites": { cat: "GYÁRTÁS", spec: "Négy évszakos" },
  "hasznalt-ponyvak": { cat: "KATALÓGUS", spec: "200+ db raktáron" },
};

export const Services = () => {
  return (
    <section
      id="szolgaltatasok"
      data-testid="services-section"
      className="relative bg-[#0B192C] text-white overflow-hidden section-pad"
    >
      {/* Tech grid background */}
      <div className="absolute inset-0 tech-grid opacity-70 pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-[#0B192C] to-transparent pointer-events-none" />
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0B192C] to-transparent pointer-events-none" />

      {/* Glow blobs */}
      <div className="absolute top-1/3 -left-32 w-[520px] h-[520px] rounded-full bg-[#EA580C]/10 blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/3 -right-32 w-[420px] h-[420px] rounded-full bg-[#1E3E62]/40 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 md:px-8">
        {/* Technical header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 md:gap-10 border-b border-white/10 pb-8">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 text-xs font-mono-tech text-[#EA580C] uppercase">
              <span className="inline-flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 live-dot" />
                <span>ONLINE</span>
              </span>
              <span className="text-white/20">│</span>
              <span>SZOLGÁLTATÁS-KATALÓGUS</span>
              <span className="text-white/20">│</span>
              <span>v{new Date().getFullYear()}</span>
            </div>
            <h2 className="mt-4 font-heading font-extrabold text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
              Operatív <span className="text-[#EA580C]">műhelymegoldások</span>.
            </h2>
          </div>
          <div className="md:max-w-sm text-right md:text-left flex md:flex-col md:items-end gap-3 md:gap-1">
            <div className="font-mono-tech text-[10px] text-white/50 uppercase tracking-[0.25em]">
              Modulok
            </div>
            <div className="font-heading font-extrabold text-white text-5xl md:text-6xl leading-none">
              {String(SERVICES.length).padStart(2, "0")}
            </div>
            <div className="font-mono-tech text-xs text-white/50 hidden md:block">
              aktív szolgáltatás
            </div>
          </div>
        </div>

        {/* Secondary meta row */}
        <div className="mt-4 mb-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-[11px] font-mono-tech uppercase tracking-widest text-white/50">
          <span>TELEPHELY · DUNAHARASZTI</span>
          <span className="text-white/20">•</span>
          <span>GYÁRTÓ · FORTUNA PONYVA KFT.</span>
          <span className="text-white/20">•</span>
          <span>ÉRVÉNYES 2025+</span>
          <span className="ml-auto text-[#EA580C]">↑ Görgetés folytatódik</span>
        </div>

        {/* Uniform 4-column catalog grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {SERVICES.map((s, i) => {
            const meta = SERVICE_META[s.id] || { cat: "SZERVIZ", spec: "—" };
            return (
              <motion.article
                key={s.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="group"
                data-testid={`service-card-${s.id}`}
              >
                <Link
                  to={s.slug}
                  className="scan-container trace-border relative h-[380px] md:h-[420px] w-full block rounded-lg overflow-hidden bg-[#0a1322] border border-white/10 hover:border-[#EA580C]/50 transition-colors"
                >
                  {/* Image */}
                  <img
                    src={s.image}
                    alt={s.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover opacity-100 group-hover:scale-105 transition-all duration-700"
                    style={{ filter: "brightness(1.15) saturate(1.08)" }}
                  />
                  {/* Dark gradient mask (lighter, only at bottom for text readability) */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a1322]/90 via-[#0a1322]/25 to-transparent" />
                  <div className="absolute inset-0 bg-gradient-to-br from-[#0B192C]/5 via-transparent to-[#EA580C]/8" />

                  {/* Scan line overlay (activates on hover) */}
                  <span className="scan-line" />

                  {/* Corner brackets */}
                  <span className="absolute top-3 left-3 w-4 h-4 border-t-2 border-l-2 border-[#EA580C]" />
                  <span className="absolute top-3 right-3 w-4 h-4 border-t-2 border-r-2 border-[#EA580C]/50" />
                  <span className="absolute bottom-3 left-3 w-4 h-4 border-b-2 border-l-2 border-[#EA580C]/50" />
                  <span className="absolute bottom-3 right-3 w-4 h-4 border-b-2 border-r-2 border-[#EA580C]" />

                  {/* Top meta bar */}
                  <div className="absolute top-0 left-0 right-0 flex items-center justify-between p-4 md:p-5 text-[10px] font-mono-tech tracking-widest text-white/70 uppercase">
                    <span className="flex items-center gap-2">
                      <span className="w-1 h-1 rounded-full bg-[#EA580C]" />
                      {String(i + 1).padStart(3, "0")} / {String(SERVICES.length).padStart(3, "0")}
                    </span>
                    <span className="px-2 py-0.5 rounded-sm border border-[#EA580C]/40 text-[#EA580C]">
                      {meta.cat}
                    </span>
                  </div>

                  {/* Content bottom */}
                  <div className="absolute inset-x-0 bottom-0 p-5 md:p-6">
                    <div className="flex items-end justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading font-bold text-white tracking-tight leading-tight text-lg md:text-xl">
                          {s.title}
                        </h3>
                      </div>
                      <div className="shrink-0 w-10 h-10 rounded-full border border-[#EA580C]/50 text-[#EA580C] flex items-center justify-center group-hover:bg-[#EA580C] group-hover:text-white group-hover:border-[#EA580C] group-hover:rotate-45 transition-all">
                        <ArrowUpRight className="w-4 h-4" />
                      </div>
                    </div>

                    {/* Spec row */}
                    <div className="mt-4 pt-3 border-t border-white/10 flex items-center text-[10px] font-mono-tech uppercase tracking-widest text-white/50">
                      <span className="flex items-center gap-1.5 truncate">
                        <Radio className="w-3 h-3 text-[#EA580C] shrink-0" />
                        <span className="truncate">{meta.spec}</span>
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.article>
            );
          })}
        </div>

        {/* Premium CTA block under the grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7 }}
          className="mt-12 md:mt-16"
        >
          <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-[#EA580C] via-[#D14D0A] to-[#C2410C] shadow-2xl">
            {/* Decorative patterns */}
            <div
              className="absolute inset-0 opacity-[0.12]"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(45deg, #fff 0 1px, transparent 1px 22px)",
              }}
            />
            <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-white/10 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-white/10 blur-3xl pointer-events-none" />

            {/* Corner brackets */}
            <span className="absolute top-4 left-4 w-6 h-6 border-t-2 border-l-2 border-white/80 rounded-tl-sm" />
            <span className="absolute top-4 right-4 w-6 h-6 border-t-2 border-r-2 border-white/80 rounded-tr-sm" />
            <span className="absolute bottom-4 left-4 w-6 h-6 border-b-2 border-l-2 border-white/80 rounded-bl-sm" />
            <span className="absolute bottom-4 right-4 w-6 h-6 border-b-2 border-r-2 border-white/80 rounded-br-sm" />

            <div className="relative grid md:grid-cols-12 gap-8 md:gap-10 p-8 md:p-12 lg:p-14 items-center">
              {/* Left – message */}
              <div className="md:col-span-7">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 border border-white/25 backdrop-blur text-white/90 text-[10px] font-mono-tech uppercase tracking-[0.3em]">
                  <span className="w-1.5 h-1.5 rounded-full bg-white live-dot" />
                  Indítás
                </div>
                <h3 className="mt-5 font-heading font-extrabold text-white text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                  Szeretne <span className="underline decoration-white/40 decoration-2 underline-offset-4">ingyenes ajánlatot</span>{" "}
                  projektjére?
                </h3>
                <p className="mt-4 text-white/90 text-base md:text-lg max-w-xl leading-relaxed">
                  Töltse ki űrlapunkat, vagy hívjon minket munkaidőben –
                  akár 24 órán belül személyre szabott árajánlatot küldünk munkanapokon.
                </p>

                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <a
                    href="#ajanlat"
                    onClick={(e) => {
                      e.preventDefault();
                      const el = document.getElementById("ajanlat");
                      if (el) el.scrollIntoView({ behavior: "smooth" });
                    }}
                    data-testid="services-cta-quote"
                    className="inline-flex items-center justify-center gap-2 h-14 px-8 rounded-md bg-[#0B192C] hover:bg-[#081422] text-white font-semibold text-base transition-colors shadow-xl"
                  >
                    Ajánlatot kérek
                    <ArrowUpRight className="w-5 h-5" />
                  </a>
                  <a
                    href={`tel:${COMPANY.phoneRaw}`}
                    className="inline-flex items-center justify-center gap-2 h-14 px-8 rounded-md border-2 border-white text-white hover:bg-white hover:text-[#C2410C] font-semibold text-base transition-colors"
                  >
                    {COMPANY.phone}
                  </a>
                </div>
              </div>

              {/* Right – tech stat card */}
              <div className="md:col-span-5">
                <div className="relative bg-[#0B192C]/30 backdrop-blur border border-white/20 rounded-xl p-6 md:p-7">
                  <div className="font-mono-tech text-[10px] text-white/60 tracking-[0.3em] uppercase">
                    Kapcsolati adatok
                  </div>
                  <div className="mt-5 space-y-4">
                    <div>
                      <div className="text-[10px] font-mono-tech text-white/60 tracking-widest uppercase">
                        [ TEL ]
                      </div>
                      <div className="mt-1 font-heading font-extrabold text-white text-2xl">
                        {COMPANY.phone}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] font-mono-tech text-white/60 tracking-widest uppercase">
                        [ CÍM ]
                      </div>
                      <div className="mt-1 font-heading font-semibold text-white text-sm leading-snug">
                        {COMPANY.address}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] font-mono-tech text-white/60 tracking-widest uppercase">
                        [ VÁLASZIDŐ ]
                      </div>
                      <div className="mt-1 font-heading font-semibold text-white text-sm">
                        Akár 24 órán belül, munkanapokon
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
